import PropTypes from 'prop-types'
import { format } from 'date-fns-tz'
import { useTranslation } from 'react-i18next'

import AudioMessageRecord from '../../../Chat/ChatSidebar/AudioMessageRecord'
import { formatDateTimeInTimezone } from '@/utils/date'

const MaintenanceRequestActivity = ({
  activity: { additionalData, createdAt, maintenanceId, variant, id },
  timezone,
}) => {
  const { t } = useTranslation(['maintenance'])
  const scheduledDate = additionalData?.newScheduledDate || additionalData?.oldScheduledDate
  const formattedTime = (
    <p className="time-text">{format(createdAt, 'MM/dd/yy, hh:mm a').toUpperCase()}</p>
  )

  const getCancelationActivity = () => (
    <div className="recent-activity-info-block">
      <div className="recent-activity-title-wrapper">
        <h3 className="recent-activity-title">
          {t('maintenance:requestPreview.activity.newStatus')}
        </h3>
        <div className="status-container canceled">
          <h3 className="recent-activity-title">
            {t('maintenance:requestPreview.activity.canceled')}
          </h3>
        </div>
      </div>
      <div className="recent-activity-body">
        {additionalData.cancellationReason && (
          <p className="body-text">
            {t('maintenance:requestPreview.activity.reason', {
              reason: additionalData.cancellationReason,
            })}
          </p>
        )}
        {additionalData.author && (
          <p className="body-text">
            {t('maintenance:requestPreview.notes.author', { author: additionalData.author })}
          </p>
        )}
        {formattedTime}
      </div>
    </div>
  )

  const getCompletionActivity = () => (
    <div className="recent-activity-info-block">
      <div className="recent-activity-title-wrapper">
        <h3 className="recent-activity-title">
          {t('maintenance:requestPreview.activity.newStatus')}
        </h3>
        <div className="status-container completed">
          <h3 className="recent-activity-title">
            {t('maintenance:requestPreview.activity.completed')}
          </h3>
        </div>
      </div>
      <div className="recent-activity-body">
        {additionalData.author && (
          <p className="body-text">
            {t('maintenance:requestPreview.notes.author', { author: additionalData.author })}
          </p>
        )}
        {formattedTime}
      </div>
    </div>
  )

  const getNewInternalNoteActivity = () => (
    <div className="recent-activity-info-block">
      <div className="recent-activity-title-wrapper">
        <h3 className="recent-activity-title">{t('maintenance:requestPreview.activity.notes')}</h3>
      </div>
      <div className="recent-activity-body">
        <p className="body-text">{additionalData.note}</p>
        {additionalData.author && (
          <p className="body-text">
            {t('maintenance:requestPreview.notes.author', { author: additionalData.author })}
          </p>
        )}
        {formattedTime}
      </div>
    </div>
  )

  const getChatMessageActivity = () => (
    <div className="recent-activity-info-block">
      <div className="recent-activity-title-wrapper">
        <h3 className="recent-activity-title">
          {t('maintenance:requestPreview.activity.chatMessage')}
        </h3>
      </div>
      <div className="recent-activity-body">
        {additionalData.author && (
          <p className="body-text">
            {t('maintenance:requestPreview.notes.author', { author: additionalData.author })}
          </p>
        )}
        {formattedTime}
      </div>
    </div>
  )

  const getRepairStartActivity = () => (
    <div className="recent-activity-info-block">
      <div className="recent-activity-title-wrapper">
        <h3 className="recent-activity-title">
          {t('maintenance:requestPreview.activity.newStatus')}
        </h3>
        <div className="status-container pending">
          <h3 className="recent-activity-title">
            {t('maintenance:requestPreview.activity.repairStart')}
          </h3>
        </div>
      </div>
      <div className="recent-activity-body">
        <p className="body-text">{t('maintenance:requestPreview.activity.repairStarted')}</p>
        {formattedTime}
      </div>
    </div>
  )

  const getScheduledActivity = () => (
    <div className="recent-activity-info-block">
      <div className="recent-activity-title-wrapper">
        <h3 className="recent-activity-title">
          {t('maintenance:requestPreview.activity.newStatus')}
        </h3>
        <div className="status-container scheduled">
          <h3 className="recent-activity-title">
            {t('maintenance:requestPreview.activity.scheduled')}
          </h3>
        </div>
      </div>
      <div className="recent-activity-body">
        <p className="body-text">
          {scheduledDate && formatDateTimeInTimezone(scheduledDate, timezone)}
        </p>
        {formattedTime}
      </div>
    </div>
  )

  const getStaffAssignedActivity = () => (
    <div className="recent-activity-info-block">
      <div className="recent-activity-title-wrapper">
        <h3 className="recent-activity-title">
          {t('maintenance:requestPreview.activity.newStatus')}
        </h3>
        <div className="status-container assigned">
          <h3 className="recent-activity-title">
            {t('maintenance:requestPreview.activity.assigned')}
          </h3>
        </div>
      </div>
      <div className="recent-activity-body">
        <p className="body-text">
          {t('maintenance:requestPreview.activity.assignee', {
            assignee: additionalData.newAssigneeName,
          })}
        </p>
        {formattedTime}
      </div>
    </div>
  )

  const getStatusUpdateActivity = () => (
    <div className="recent-activity-info-block">
      <div className="recent-activity-title-wrapper">
        <h3 className="recent-activity-title">
          {t('maintenance:requestPreview.activity.newStatus')}
        </h3>
        <div className="status-container pending">
          <h3 className="recent-activity-title">{additionalData.newStatus}</h3>
        </div>
      </div>
      <div className="recent-activity-body">
        <p className="body-text">{additionalData.newStatus}</p>
        {formattedTime}
      </div>
    </div>
  )

  const getSubmissionActivity = () => (
    <div className="recent-activity-info-block">
      <div className="recent-activity-title-wrapper">
        <h3 className="recent-activity-title">
          {t('maintenance:requestPreview.activity.submission')}
        </h3>
      </div>
      <div className="recent-activity-body">{formattedTime}</div>
    </div>
  )

  const getViocemailActivity = () => (
    <div className="recent-activity-info-block">
      <div className="recent-activity-title-wrapper">
        <h3 className="recent-activity-title">
          {t('maintenance:requestPreview.activity.voicemail')}
        </h3>
      </div>
      <div className="recent-activity-body">
        <AudioMessageRecord
          recordingUrl={additionalData.recordingUrl}
          transcriptionBegin={additionalData.transcription}
        />
        {formattedTime}
      </div>
    </div>
  )

  const functionsForTypes = {
    cancelation: getCancelationActivity,
    completion: getCompletionActivity,
    new_internal_note: getNewInternalNoteActivity,
    repair_start: getRepairStartActivity,
    scheduled: getScheduledActivity,
    staff_assigned: getStaffAssignedActivity,
    status_update: getStatusUpdateActivity,
    submission: getSubmissionActivity,
    new_chat_message: getChatMessageActivity,
    voicemail: getViocemailActivity,
  }

  return (
    <div key={`${id}-${maintenanceId}`} className="recent-activity-block">
      <div className="icon-chain">
        <div className="icon-wrapper" />
        <div className="dotted-spaced" />
      </div>
      {functionsForTypes[variant]()}
    </div>
  )
}

MaintenanceRequestActivity.propTypes = {
  activity: PropTypes.object,
  timezone: PropTypes.string,
}

export default MaintenanceRequestActivity
